/**
 * URL-safe id obfuscation.
 *
 * The live API uses bare UUIDs as primary keys. Surfacing them in the
 * browser address bar leaks tenant data structure to anyone looking
 * over the user's shoulder ("/panel/products/22222222-3333-…"). We
 * base64url-encode the id so the URL reads as opaque
 * ("/panel/products/MjIyMjIyMjItMzMzMy0w…") while still being a real,
 * shareable URL.
 *
 * This is OBFUSCATION, not encryption — anyone with DevTools or the
 * encoder can recover the UUID. The goal is "don't disclose to casual
 * end users", not "treat as a secret". Refresh + deep-link still work.
 */

export function encodeId(id: string | null | undefined): string {
  if (!id) return '';
  // The reserved 'new' sentinel is the create-form route — never encode it.
  if (id === 'new') return 'new';
  try {
    // btoa handles ASCII-only strings; UUIDs are ASCII so no encodeURIComponent
    // dance is required. We then translate to base64url so the value is
    // safe to embed directly in a URL path segment.
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch {
    return id;
  }
}

export function decodeId(opaque: string | null | undefined): string {
  if (!opaque) return '';
  if (opaque === 'new') return 'new';
  // If the value already looks like a UUID, pass it through. This keeps
  // backwards compatibility with any legacy bookmarks.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(opaque)) {
    return opaque;
  }
  try {
    // Pad to multiple of 4 then decode base64url → ASCII.
    const b64 = opaque.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return atob(padded);
  } catch {
    return opaque;
  }
}
